import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ORDER_STATUS_LABELS } from '../orderStatus';
import { api } from '../api';
import OrderModal from '../components/OrderModal';
import RouteMap, { clusterColor } from '../components/RouteMap';
import CaSelect from '../components/CaSelect';
import useCourierLocationsStream from '../hooks/useCourierLocationsStream';
import '../styles/CourierAssignmentPage.css';
import { useDeliverySlots, unionWithOrderSlots } from '../deliverySlots';
import { openOrdersStream } from '../utils/ordersStream';

const statusLabels = ORDER_STATUS_LABELS; // единые подписи (src/orderStatus.js)
const statusColors = {
  new: { bg: '#DEEFFF', color: '#26689A' },
  packaging: { bg: '#F9EBCF', color: '#9A4820' },
  ready: { bg: '#DCF4E0', color: '#1F763B' },
  courier: { bg: '#F3E5F5', color: '#7B1FA2' },
  completed: { bg: '#E0F2F1', color: '#00695C' },
};

const trafficTip = 'Время плеч OSRM считает без пробок, поэтому к нему добавлен коэффициент на момент старта маршрута: будни 07–10 и 16–20 — +25%, остальное дневное время — +10%, выходные — +5%, ночь — без надбавки. Влияет на ETA, минуты и превышение слота; порядок объезда не меняет. Значения общие для платформы, для инстанса настраиваются в .env (TRAFFIC_FACTORS)';

// Иконки панели — inline SVG в одном стиле (квадратные концы линий, как у логотипа)
const I = {
  prev: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><path d="M15 18l-6-6 6-6" /></svg>,
  next: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><path d="M9 18l6-6-6-6" /></svg>,
  minus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  sliders: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /><rect x="7" y="4" width="4" height="4" fill="currentColor" stroke="none" /><rect x="13" y="10" width="4" height="4" fill="currentColor" stroke="none" /><rect x="6" y="16" width="4" height="4" fill="currentColor" stroke="none" /></svg>,
  grid: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>,
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></svg>,
  up: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><path d="M18 15l-6-6-6 6" /></svg>,
  eye: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><rect x="4" y="4" width="16" height="16" /><circle cx="12" cy="12" r="3" /></svg>,
  clock: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square" strokeLinejoin="miter"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>,
};

function CourierAssignmentPage({ showToast }) {
  const { timeSlots: configSlots } = useDeliverySlots();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  // Flat order list (before clustering)
  const [slotOrders, setSlotOrders] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotLoaded, setSlotLoaded] = useState(false);
  // Clustered data
  const [clusters, setClusters] = useState([]);
  const [distantOrders, setDistantOrders] = useState([]);
  const [distantKm, setDistantKm] = useState(20); // порог «дальнего» — радиус доставки из настроек
  const [unassignable, setUnassignable] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [freeOrders, setFreeOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [numActiveCouriers, setNumActiveCouriers] = useState(0);
  const [clusterCouriers, setClusterCouriers] = useState({});
  const [manualCouriers, setManualCouriers] = useState({});
  const [distantCouriers, setDistantCouriers] = useState({});
  const [freeCouriers, setFreeCouriers] = useState({});
  const [freeGroupAssign, setFreeGroupAssign] = useState({});
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [built, setBuilt] = useState(false);
  const [numClustersInput, setNumClustersInput] = useState('');
  const [balanced, setBalanced] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  // Карта: маршруты назначенных групп (с бэка, по delivery_sequence), склад, движок
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [warehouse, setWarehouse] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [mapFitKey, setMapFitKey] = useState(0);
  // Какие группы скрыты на карте (id из mapClusters, 'points' — заказы без группы)
  const [hiddenGroups, setHiddenGroups] = useState([]);
  // Панель: счётчики заказов по слотам на дату, поповер настроек, подсветка группы по наведению
  const [slotCounts, setSlotCounts] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [hoverGroup, setHoverGroup] = useState(null);

  // useCallback обязателен: ссылка уходит в RouteMap как onOrderClick, новая ссылка на
  // каждый рендер пересоздавала все слои карты (мигание при наведении на карточки)
  const openOrderModal = useCallback(async (orderId) => {
    try {
      const order = await api.getOrderById(orderId);
      setSelectedOrder(order);
      setShowOrderModal(true);
    } catch (e) {
      console.error('Failed to load order:', e);
    }
  }, []);

  useEffect(() => {
    loadCouriers();
  }, []);

  const loadCouriers = async () => {
    try {
      const data = await api.getEmployees('courier');
      setCouriers(data.filter(c => c.is_active));
    } catch (e) {
      console.error('Failed to load couriers', e);
    }
  };

  const goToPrevious = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
    resetAll();
  };
  const goToNext = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
    resetAll();
  };
  const goToToday = () => {
    setSelectedDate(new Date());
    resetAll();
  };

  const resetAll = () => {
    setBuilt(false);
    setSlotLoaded(false);
    setSlotOrders([]);
    setClusters([]);
    setFreeOrders([]);
  };

  const formatDate = (date) =>
    date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' });
  // «09:00-11:00» → «09:00–11:00» (у слотов бывают минуты — показываем полностью)
  const shortSlot = (slot) => (slot || '').replace('-', '–');

  // Форматируем дату в локальном часовом поясе (не UTC)
  const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

  // Гонка ответов при быстром переключении дат (← →): применяем только ответ последнего
  // запроса, иначе в стейте оседают данные не за ту дату
  const reqRef = useRef(0);

  // Актуальные значения для SSE-обработчика (подписка ставится один раз)
  const liveRef = useRef({ dateStr, selectedSlot, slotLoaded, built });

  // Маршруты назначенных групп для карты (геометрия по дорогам + ETA).
  // Свой счётчик гонок здесь не нужен: ответ применяем, только если дата и слот всё те же.
  // Фоновые обновления (SSE от чужих правок, позиции курьеров, отложенный throttle-таймер)
  // приходят с тем слотом, который был на момент запроса — без этой проверки они
  // подсовывали в открытый экран группы другого слота, и заказы текущего «пропадали».
  const loadAssignedRoutes = useCallback(async (slot) => {
    try {
      const data = await api.getAssignedRoutes(dateStr, slot);
      const live = liveRef.current;
      if (live.dateStr !== dateStr || (live.selectedSlot ?? null) !== (slot ?? null)) return;
      setAssignedRoutes(data.groups || []);
      setWarehouse(data.warehouse || null);
    } catch (e) {
      console.error('Failed to load assigned routes', e);
    }
  }, [dateStr]);
  useEffect(() => {
    liveRef.current = { dateStr, selectedSlot, slotLoaded, built };
  }, [dateStr, selectedSlot, slotLoaded, built]);
  const loadAssignedRoutesRef = useRef(loadAssignedRoutes);
  useEffect(() => { loadAssignedRoutesRef.current = loadAssignedRoutes; }, [loadAssignedRoutes]);
  const loadSlotCountsRef = useRef(null);

  // Тихое обновление плоского списка (без сброса построенных групп и выбранных курьеров)
  const refreshSlotOrders = useCallback(async () => {
    const { dateStr: d, selectedSlot: s } = liveRef.current;
    try {
      const data = await api.getOrdersBySlot(d, s);
      setSlotOrders(data.orders || []);
    } catch (e) {
      console.error('Failed to refresh slot orders', e);
    }
  }, []);

  // SSE: «Доставлен» у курьера, назначение, новый заказ → список и маршруты обновляются
  // сами; раньше плоский список грузился один раз и доставленный заказ «висел» как У курьера
  useEffect(() => {
    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream
    const es = openOrdersStream();
    let timer = null;
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if ((data.type === 'new_order' || data.type === 'order_updated') && data.delivery_date === liveRef.current.dateStr) {
          clearTimeout(timer);
          // Пауза со случайной добавкой: у нескольких открытых панелей волны обновлений
          // иначе приходят в одну и ту же секунду. Своё действие видно сразу — обработчики
          // кнопок обновляют данные сами, не дожидаясь события
          timer = setTimeout(() => {
            const { selectedSlot: s, slotLoaded: sl, built: b } = liveRef.current;
            if (sl) refreshSlotOrders();
            if (sl || b) loadAssignedRoutesRef.current?.(s);
            loadSlotCountsRef.current?.();
          }, 1200 + Math.floor(Math.random() * 600));
        }
      } catch (e) { /* мусор в потоке */ }
    };
    return () => { clearTimeout(timer); es.close(); };
  }, [refreshSlotOrders]);

  // Счётчики заказов по слотам на выбранную дату (для чипов слотов).
  // Отдельный лёгкий эндпоинт: раньше ради этих цифр тянулись все заказы дня целиком
  const loadSlotCounts = useCallback(async () => {
    try {
      const data = await api.getSlotCounts(liveRef.current.dateStr);
      setSlotCounts(data.counts || {});
    } catch (e) {
      console.error('Failed to load slot counts', e);
    }
  }, []);
  useEffect(() => { loadSlotCountsRef.current = loadSlotCounts; }, [loadSlotCounts]);
  useEffect(() => { loadSlotCounts(); }, [dateStr, loadSlotCounts]);

  // Load flat order list when slot is clicked
  const loadSlotOrders = useCallback(async (slot) => {
    setSlotLoading(true);
    setBuilt(false);
    setClusters([]);
    setFreeOrders([]);
    const my = ++reqRef.current;
    try {
      const data = await api.getOrdersBySlot(dateStr, slot);
      if (my !== reqRef.current) return;
      setSlotOrders(data.orders || []);
      setSlotLoaded(true);
      setHiddenGroups([]);
      await loadAssignedRoutes(slot);
      setMapFitKey(k => k + 1);
    } catch (e) {
      console.error('Failed to load slot orders', e);
      showToast?.('Ошибка загрузки заказов', 'error');
    } finally {
      setSlotLoading(false);
    }
  }, [dateStr, showToast, loadAssignedRoutes]);

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    loadSlotOrders(slot);
  };

  // Первый вход и смена даты: сразу грузим заказы выбранного слота (по умолчанию —
  // «Все слоты»); раньше список появлялся только после клика по чипу слота
  useEffect(() => {
    loadSlotOrders(selectedSlot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  // Build clusters
  const handleBuildClusters = async () => {
    setLoading(true);
    setBuilt(false);
    setSlotLoaded(false);
    try {
      // В авто-режиме шлём то самое число, что показано на кнопке («Авто · N» — активные
      // курьеры без маршрута в этом слоте). Пустое значение бэкенд трактовал как «все
      // активные курьеры», занятых не вычитал — групп выходило больше, чем обещала кнопка
      const data = await api.getClusters(dateStr, selectedSlot, 15, 7, routesValue || autoRoutes, balanced);
      setClusters(data.clusters || []);
      setDistantOrders(data.distant_orders || []);
      setDistantKm(data.distant_threshold_km || 20);
      setUnassignable(data.unassignable || []);
      setAssignedGroups(data.assigned_groups || []);
      setFreeOrders([]);
      setNumActiveCouriers(data.num_active_couriers || 0);
      if (data.available_couriers) {
        setCouriers(data.available_couriers);
      }
      setClusterCouriers({});
      setManualCouriers({});
      setDistantCouriers({});
      setFreeCouriers({});
      setFreeGroupAssign({});
      setBuilt(true);
      setHiddenGroups([]);
      await loadAssignedRoutes(selectedSlot);
      setMapFitKey(k => k + 1);

      const totalNew = (data.clusters || []).reduce((s, c) => s + c.order_count, 0)
        + (data.distant_orders || []).length
        + (data.unassignable || []).length;
      const totalAssigned = (data.assigned_groups || []).reduce((s, g) => s + g.orders.length, 0);

      if (totalNew === 0 && totalAssigned === 0) {
        showToast?.('Нет заказов на эту дату', 'error');
      } else {
        const parts = [];
        if (totalNew > 0) parts.push(`${totalNew} новых`);
        if (totalAssigned > 0) parts.push(`${totalAssigned} назначенных`);
        showToast?.(`${parts.join(', ')} | ${(data.clusters || []).length} групп | ${data.num_active_couriers} курьеров`);
      }
    } catch (e) {
      console.error('Failed to build clusters', e);
      showToast?.('Ошибка при группировке', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Remove order from cluster -> free orders
  // Пока идёт пересчёт, повторный клик по «×»/«+» дублировал заказ (в стейте до ответа
  // он ещё виден в старом списке)
  const busyOrdersRef = useRef(new Set());

  const handleRemoveFromCluster = async (clusterId, orderId) => {
    if (busyOrdersRef.current.has(orderId)) return;
    const sourceCluster = clusters.find(c => c.cluster_id === clusterId);
    if (!sourceCluster) return;
    busyOrdersRef.current.add(orderId);

    const removedOrder = sourceCluster.orders.find(o => o.id === orderId);
    if (!removedOrder) return;

    // Find nearest cluster by geographic proximity (haversine to closest order in each cluster)
    let nearestCluster = null;
    let minDist = Infinity;
    if (removedOrder.delivery_lat && removedOrder.delivery_lon) {
      for (const c of clusters) {
        if (c.cluster_id === clusterId) continue;
        for (const o of c.orders) {
          if (o.delivery_lat && o.delivery_lon) {
            const dlat = removedOrder.delivery_lat - o.delivery_lat;
            const dlon = removedOrder.delivery_lon - o.delivery_lon;
            const dist = dlat * dlat + dlon * dlon;
            if (dist < minDist) {
              minDist = dist;
              nearestCluster = c.cluster_id;
            }
          }
        }
      }
    }

    setFreeOrders(fo => [...fo, { ...removedOrder, recommended_cluster: nearestCluster }]);

    const remainingOrders = sourceCluster.orders.filter(o => o.id !== orderId);
    // Убираем из группы сразу (оптимистично) — иначе строка с «×» видна до ответа OSRM
    setClusters(prev => prev
      .map(c => (c.cluster_id === clusterId ? { ...c, orders: remainingOrders, order_count: remainingOrders.length } : c))
      .filter(c => c.order_count > 0));

    if (remainingOrders.length === 0) {
      // Группа опустела — снимаем её выбор курьера и отметку скрытия на карте
      setClusterCouriers(prev => { const next = { ...prev }; delete next[clusterId]; return next; });
      setHiddenGroups(prev => prev.filter(id => id !== `c${clusterId}`));
      busyOrdersRef.current.delete(orderId);
      return;
    }

    // Пересчитываем маршрут для оставшихся заказов
    try {
      const remainingIds = remainingOrders.map(o => o.id);
      const result = await api.reoptimizeCluster(remainingIds);

      const sequenceMap = {};
      result.orders.forEach(o => {
        sequenceMap[o.id] = {
          delivery_sequence: o.delivery_sequence,
          estimated_arrival: o.estimated_arrival,
          distance_from_warehouse_km: o.distance_from_warehouse_km,
          eta_late: o.eta_late,
          eta_deadline: o.eta_deadline,
        };
      });

      setClusters(prev => prev.map(cluster => {
        if (cluster.cluster_id !== clusterId) return cluster;
        const updatedOrders = remainingOrders.map(o => ({
          ...o,
          delivery_sequence: sequenceMap[o.id]?.delivery_sequence || o.delivery_sequence,
          estimated_arrival: sequenceMap[o.id]?.estimated_arrival || null,
          distance_from_warehouse_km: sequenceMap[o.id]?.distance_from_warehouse_km || o.distance_from_warehouse_km,
          eta_late: sequenceMap[o.id]?.eta_late || false,
          eta_deadline: sequenceMap[o.id]?.eta_deadline || o.eta_deadline,
        }));
        updatedOrders.sort((a, b) => (a.delivery_sequence || 0) - (b.delivery_sequence || 0));
        return {
          ...cluster,
          orders: updatedOrders,
          order_count: updatedOrders.length,
          total_distance_km: result.total_distance_km ?? cluster.total_distance_km,
          total_duration_min: result.total_duration_min ?? cluster.total_duration_min,
          geometry: result.geometry ?? null,
          slot_overrun_min: result.slot_overrun_min ?? null,
          return_km: result.return_km ?? null,
        };
      }));
    } catch (error) {
      console.error('Failed to reoptimize after removal:', error);
      showToast?.('Заказ убран, маршрут не пересчитан', 'error');
    } finally {
      busyOrdersRef.current.delete(orderId);
    }
  };

  // Add free order back to a cluster
  // Заказ из «свободных» или «дальних» → в выбранную группу с пересчётом её маршрута
  const handleAddToCluster = async (orderId, clusterId) => {
    if (busyOrdersRef.current.has(orderId)) return;
    const order = freeOrders.find(o => o.id === orderId) || distantOrders.find(o => o.id === orderId);
    if (!order || !clusterId) return;
    busyOrdersRef.current.add(orderId);
    const dropFromLists = () => {
      setFreeOrders(prev => prev.filter(o => o.id !== orderId));
      setDistantOrders(prev => prev.filter(o => o.id !== orderId));
      setDistantCouriers(prev => { const next = { ...prev }; delete next[orderId]; return next; });
    };

    const targetCluster = clusters.find(c => c.cluster_id === parseInt(clusterId));
    if (!targetCluster) return;

    // Собираем все ID заказов в группе включая новый
    const allOrderIds = [...targetCluster.orders.map(o => o.id), orderId];

    try {
      // Вызываем API для пересчёта оптимального маршрута
      const result = await api.reoptimizeCluster(allOrderIds);

      // Один путь применения ответа (в т.ч. eta_late/eta_deadline — без них бейдж
      // «опаздывают» не появлялся именно после ручного добавления адреса)
      applyClusterResult(parseInt(clusterId, 10), [...targetCluster.orders, order], result);

      dropFromLists();
      setFreeGroupAssign(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });

      showToast?.('Маршрут пересчитан с учётом нового адреса');
    } catch (error) {
      console.error('Failed to reoptimize cluster:', error);
      showToast?.('Ошибка пересчёта маршрута', 'error');

      // Fallback: просто добавляем заказ в конец
      setClusters(prev => prev.map(cluster => {
        if (cluster.cluster_id !== parseInt(clusterId)) return cluster;
        return {
          ...cluster,
          orders: [...cluster.orders, order],
          order_count: cluster.order_count + 1,
        };
      }));
      dropFromLists();
      setFreeGroupAssign(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    } finally {
      busyOrdersRef.current.delete(orderId);
    }
  };

  const handleAssign = async () => {
    const assignments = [];

    for (const cluster of clusters) {
      const courierId = clusterCouriers[cluster.cluster_id];
      if (courierId) {
        assignments.push({
          order_ids: cluster.orders.map(o => o.id),
          courier_id: parseInt(courierId),
        });
      }
    }

    // Distant orders
    const distantByCorier = {};
    for (const [orderId, courierId] of Object.entries(distantCouriers)) {
      if (courierId) {
        if (!distantByCorier[courierId]) distantByCorier[courierId] = [];
        distantByCorier[courierId].push(parseInt(orderId));
      }
    }
    for (const [courierId, orderIds] of Object.entries(distantByCorier)) {
      assignments.push({ order_ids: orderIds, courier_id: parseInt(courierId) });
    }

    // Manual (unassignable)
    const manualByCorier = {};
    for (const [orderId, courierId] of Object.entries(manualCouriers)) {
      if (courierId) {
        if (!manualByCorier[courierId]) manualByCorier[courierId] = [];
        manualByCorier[courierId].push(parseInt(orderId));
      }
    }
    for (const [courierId, orderIds] of Object.entries(manualByCorier)) {
      assignments.push({ order_ids: orderIds, courier_id: parseInt(courierId) });
    }

    // Free orders (removed from groups, assigned individually)
    const freeByCorier = {};
    for (const [orderId, courierId] of Object.entries(freeCouriers)) {
      if (courierId) {
        if (!freeByCorier[courierId]) freeByCorier[courierId] = [];
        freeByCorier[courierId].push(parseInt(orderId));
      }
    }
    for (const [courierId, orderIds] of Object.entries(freeByCorier)) {
      assignments.push({ order_ids: orderIds, courier_id: parseInt(courierId) });
    }

    if (assignments.length === 0) {
      showToast?.('Выберите курьеров для назначения', 'error');
      return;
    }

    setAssigning(true);
    try {
      const result = await api.assignCouriers(assignments);
      showToast?.(result.refit_groups
        ? `Назначено ${result.updated_count} заказов, маршрутов пересчитано: ${result.refit_groups}`
        : `Назначено ${result.updated_count} заказов`);
      // На месте, без пересборки групп: назначенные группы/заказы уходят из списков,
      // остальные группы и выбранные курьеры остаются, маршруты курьеров перечитываются
      const assignedIds = new Set(assignments.flatMap(a => a.order_ids));
      const assignedClusterIds = new Set(clusters.filter(c => clusterCouriers[c.cluster_id]).map(c => c.cluster_id));
      const strip = (obj) => Object.fromEntries(Object.entries(obj).filter(([k]) => !assignedIds.has(parseInt(k, 10))));
      setClusters(prev => prev.filter(c => !assignedClusterIds.has(c.cluster_id)));
      setDistantOrders(prev => prev.filter(o => !assignedIds.has(o.id)));
      setUnassignable(prev => prev.filter(o => !assignedIds.has(o.id)));
      setFreeOrders(prev => prev.filter(o => !assignedIds.has(o.id)));
      setClusterCouriers(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !assignedClusterIds.has(parseInt(k, 10)))));
      setDistantCouriers(strip);
      setManualCouriers(strip);
      setFreeCouriers(strip);
      setFreeGroupAssign(strip);
      setHiddenGroups(prev => prev.filter(id => !(String(id).startsWith('c') && assignedClusterIds.has(parseInt(String(id).slice(1), 10)))));
      await loadAssignedRoutes(selectedSlot);
      loadSlotCounts();
    } catch (e) {
      console.error('Failed to assign couriers', e);
      showToast?.('Ошибка при назначении', 'error');
    } finally {
      setAssigning(false);
    }
  };

  // Пересчёт и сохранение порядка для уже назначенной группы (persist=true)
  const handleRecalcAssigned = async (group) => {
    const ids = group.orders
      .filter(o => o.status !== 'completed' && o.status !== 'cancelled')
      .map(o => o.id);
    if (ids.length < 2) {
      showToast?.('В группе меньше двух активных заказов', 'error');
      return;
    }
    try {
      await api.reoptimizeCluster(ids, true);
      showToast?.('Маршрут пересчитан и сохранён');
      await loadAssignedRoutes(selectedSlot);
    } catch (e) {
      console.error('Failed to recalc assigned route', e);
      showToast?.('Ошибка пересчёта маршрута', 'error');
    }
  };

  // Правка заказа в карточке (окно клиента, адрес, курьер, слот): группу с этим заказом
  // пересчитываем на месте, маршруты курьеров перечитываем — без пересборки всех групп
  const handleOrderUpdated = async () => {
    const id = selectedOrder?.id;
    loadAssignedRoutes(selectedSlot);
    loadSlotCounts();
    if (!built) {
      if (slotLoaded) refreshSlotOrders();
      return;
    }
    const cluster = clusters.find(c => c.orders.some(o => o.id === id));
    if (!cluster) return;
    try {
      const fresh = await api.getOrderById(id);
      const leaves = fresh.courier_id || fresh.status === 'cancelled'
        || fresh.delivery_date !== dateStr
        || (selectedSlot && fresh.delivery_time !== selectedSlot);
      const rest = cluster.orders.filter(o => o.id !== id);
      if (leaves) {
        setClusters(prev => prev
          .map(c => (c.cluster_id === cluster.cluster_id ? { ...c, orders: rest, order_count: rest.length } : c))
          .filter(c => c.order_count > 0));
        if (rest.length >= 2) {
          const result = await api.reoptimizeCluster(rest.map(o => o.id));
          applyClusterResult(cluster.cluster_id, rest, result);
        }
        return;
      }
      const merged = cluster.orders.map(o => (o.id === id ? {
        ...o,
        status: fresh.status,
        delivery_time: fresh.delivery_time,
        deliver_from: fresh.deliver_from,
        deliver_to: fresh.deliver_to,
        courier_comment: fresh.courier_comment,
        delivery_address: fresh.delivery_address,
        apartment: fresh.apartment,
        delivery_lat: fresh.delivery_lat ?? o.delivery_lat,
        delivery_lon: fresh.delivery_lon ?? o.delivery_lon,
      } : o));
      const result = await api.reoptimizeCluster(merged.map(o => o.id));
      applyClusterResult(cluster.cluster_id, merged, result);
    } catch (e) {
      console.error('Failed to refresh cluster after order edit', e);
    }
  };

  // Ответ /reoptimize-cluster → порядок, ETA, геометрия и итоги группы в стейте
  const applyClusterResult = (clusterId, orders, result) => {
    const byId = {};
    (result.orders || []).forEach(o => { byId[o.id] = o; });
    const updated = orders.map(o => ({
      ...o,
      delivery_sequence: byId[o.id]?.delivery_sequence ?? o.delivery_sequence,
      estimated_arrival: byId[o.id]?.estimated_arrival ?? null,
      distance_from_warehouse_km: byId[o.id]?.distance_from_warehouse_km ?? o.distance_from_warehouse_km,
      eta_late: byId[o.id]?.eta_late || false,
      eta_deadline: byId[o.id]?.eta_deadline ?? o.eta_deadline,
    }));
    updated.sort((a, b) => (a.delivery_sequence || 0) - (b.delivery_sequence || 0));
    setClusters(prev => prev.map(c => (c.cluster_id !== clusterId ? c : {
      ...c,
      orders: updated,
      order_count: updated.length,
      total_distance_km: result.total_distance_km ?? c.total_distance_km,
      total_duration_min: result.total_duration_min ?? c.total_duration_min,
      geometry: result.geometry ?? c.geometry,
      slot_overrun_min: result.slot_overrun_min ?? null,
      // null — осознанный ответ бэкенда (последний слот суток, возврата нет), а не «нет
      // данных»: старое значение оставлять нельзя, иначе в шапке висит «+N км до склада»
      return_km: result.return_km ?? null,
    })));
  };

  // Данные для карты: новые группы, назначенные маршруты, свободные/дальние точки
  const mapClusters = useMemo(() => {
    const fresh = built
      ? clusters.map((c, i) => ({ id: `c${c.cluster_id}`, color: clusterColor(i), label: `Группа ${c.cluster_id}`, orders: c.orders, geometry: c.geometry }))
      : [];
    const assigned = assignedRoutes.map((g, i) => ({
      id: `a${g.courier_id}`, color: clusterColor(fresh.length + i), label: g.courier_name, orders: g.orders, geometry: g.geometry,
    }));
    return [...fresh, ...assigned];
  }, [built, clusters, assignedRoutes]);

  const mapPoints = useMemo(() => (
    built ? [...freeOrders, ...distantOrders] : slotOrders.filter(o => !o.courier_id)
  ), [built, freeOrders, distantOrders, slotOrders]);

  // Выбор групп на карте: клик по легенде — включить/выключить, «На карте» в карточке — только она
  const isHidden = (id) => hiddenGroups.includes(id);
  const toggleGroup = (id) => {
    setHiddenGroups(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    setMapFitKey(k => k + 1);
  };
  const soloGroup = (id) => {
    const all = [...mapClusters.map(c => c.id), 'points'];
    setHiddenGroups(all.filter(x => x !== id));
    setShowMap(true);
    setMapFitKey(k => k + 1);
    document.querySelector('.ca-map-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const showAllGroups = () => {
    setHiddenGroups([]);
    setMapFitKey(k => k + 1);
  };
  const visibleClusters = useMemo(() => mapClusters.filter(c => !hiddenGroups.includes(c.id)), [mapClusters, hiddenGroups]);
  const visiblePoints = useMemo(() => (hiddenGroups.includes('points') ? [] : mapPoints), [mapPoints, hiddenGroups]);

  // Живые позиции курьеров (SSE) — только пока карта на экране
  const liveCouriers = useCourierLocationsStream(showMap && (built || slotLoaded));
  const onlineCount = liveCouriers.filter(c => !c.stale && (c.age_s == null || c.age_s < 600)).length;

  // Курьер сдвинулся → через 20 с пересчитать маршруты назначенных групп (линия и ETA
  // от его текущей позиции), не чаще раза в 20 с при потоке событий
  const liveKey = liveCouriers.map(c => `${c.id}:${c.updated_at}`).join('|');
  const liveLastRunRef = useRef(0);
  const liveTimerRef = useRef(null);
  useEffect(() => {
    if (!liveKey || !(built || slotLoaded)) return undefined;
    if (liveTimerRef.current) return undefined; // уже запланировано — не сбрасываем (это throttle, не debounce)
    // + случайная добавка: панели разных админов не должны дергать пересчёт синхронно
    const wait = Math.max(0, 20000 - (Date.now() - liveLastRunRef.current)) + Math.floor(Math.random() * 4000);
    liveTimerRef.current = setTimeout(() => {
      liveTimerRef.current = null;
      liveLastRunRef.current = Date.now();
      // Слот берём на момент срабатывания, а не на момент постановки таймера: за эти
      // 20 секунд диспетчер мог переключить слот, и старое значение подменяло ему экран
      loadAssignedRoutes(liveRef.current.selectedSlot);
    }, wait);
    return undefined;
  }, [liveKey, built, slotLoaded, loadAssignedRoutes]);
  useEffect(() => () => { if (liveTimerRef.current) clearTimeout(liveTimerRef.current); }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.updateOrderStatusFromAssignment(orderId, newStatus);
      showToast?.(newStatus === 'courier' ? 'Статус: У курьера' : 'Статус: Доставлен');
      // Не пересобираем группы: это сбрасывало уже выбранных курьеров. Заказ со статусом
      // живёт в блоке «Назначенные» — достаточно перечитать маршруты и плоский список
      loadAssignedRoutes(selectedSlot);
      if (slotLoaded) refreshSlotOrders();
    } catch (e) {
      console.error('Status update failed', e);
      // Текст сервера — понятный: «Добавьте фото…», «Заказ не оплачен…»
      showToast?.(e?.message || 'Ошибка при смене статуса', 'error');
    }
  };

  const assignedCount = Object.values(clusterCouriers).filter(Boolean).length
    + Object.values(manualCouriers).filter(Boolean).length
    + Object.values(distantCouriers).filter(Boolean).length
    + Object.values(freeCouriers).filter(Boolean).length;

  // Кому везём: получатель (если не сам заказчик) → имя из профиля → имя из заказа → телефон
  const contactName = (order) => {
    if (order.is_recipient_other && order.recipient_name) return order.recipient_name;
    const u = order.user;
    const fromUser = u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '';
    return fromUser || order.customer_name || order.recipient_phone || order.phone || '—';
  };

  const renderStatusActions = (order) => {
    return (
      <>
        {order.status === 'ready' && order.courier_id && (
          <button
            className="ca-status-btn ca-status-courier"
            onClick={() => handleStatusChange(order.id, 'courier')}
          >
            У курьера
          </button>
        )}
        {order.status === 'courier' && (
          <button
            className="ca-status-btn ca-status-completed"
            onClick={() => handleStatusChange(order.id, 'completed')}
          >
            Доставлен
          </button>
        )}
      </>
    );
  };

  // Номер: у подписочной доставки — «<подписка>/<номер доставки>» (свой order_number пустой)
  const orderLabel = (o) => (o.order_type === 'subscription' || o.subscription_id
    ? `${o.subscription_order_number || o.subscription_id}/${o.subscription_delivery_number || '?'}`
    : (o.order_number || o.id));

  // Строка заказа. Колонки фиксированной ширины (№, статус, слот, окно клиента, ETA, км,
  // контакт) — пустые ячейки рендерятся всё равно, чтобы «❗», окно и ETA не сдвигали
  // соседние строки; необязательные бейджи (вне окна, флорист, сборщик, курьер) — справа,
  // перед действиями. options.actions — свои действия строки (списки курьера/группы).
  const renderOrderRow = (order, options = {}) => {
    const { showSeq = false, showCourier = false, clusterId = null, showRemove = false, actions = null, badge = null,
            seqColor = null } = options;
    const hasWindow = !!(order.deliver_from || order.deliver_to);
    return (
      <div key={order.id} className="ca-order-row">
        {showSeq && (
          <span className="ca-seq-cell">
            {order.status === 'completed'
              ? <span className="ca-seq ca-seq-done" title="Доставлен">✓</span>
              /* Номер точки — цветом группы, как её маркеры и легенда на карте */
              : (order.delivery_sequence
                  ? <span className="ca-seq" style={seqColor ? { background: seqColor } : undefined}>{order.delivery_sequence}</span>
                  : null)}
          </span>
        )}
        <span className="ca-order-id-cell">
          <span className="ca-order-id" onClick={() => openOrderModal(order.id)}>#{orderLabel(order)}</span>
          {order.courier_comment && <span className="ca-flag" title={order.courier_comment}>❗</span>}
        </span>
        <span className="ca-status-badge" style={{
          background: statusColors[order.status]?.bg,
          color: statusColors[order.status]?.color,
        }}>
          {statusLabels[order.status] || order.status}
        </span>
        <span className="ca-order-time">{order.delivery_time || '---'}</span>
        <span className="ca-window-cell">
          {hasWindow && (
            <span className="ca-window" title={`Просьба клиента: ${order.deliver_from ? `не раньше ${order.deliver_from}` : ''}${order.deliver_from && order.deliver_to ? ', ' : ''}${order.deliver_to ? `не позже ${order.deliver_to}` : ''}`}>
              {I.clock}{order.deliver_from && order.deliver_to
                ? `${order.deliver_from}–${order.deliver_to}`
                : (order.deliver_from ? `с ${order.deliver_from}` : `до ${order.deliver_to}`)}
            </span>
          )}
        </span>
        <span className="ca-eta-cell">
          {order.estimated_arrival && (
            <span
              className={`ca-eta ${order.eta_late ? 'ca-eta-late' : ''}`}
              title={order.eta_late ? `Опоздание: обещали до ${order.eta_deadline}` : (order.eta_deadline ? `Успеваем до ${order.eta_deadline}` : '')}
            >
              ~{order.estimated_arrival}{order.eta_late ? ' ⚠' : ''}
            </span>
          )}
        </span>
        <span className="ca-distance-cell">
          {order.distance_from_warehouse_km != null && (
            <span className={`ca-distance ${order.distance_from_warehouse_km > distantKm ? 'ca-distance-far' : ''}`}>
              {order.distance_from_warehouse_km} км
            </span>
          )}
        </span>
        <span className="ca-order-contact">
          {contactName(order)}
        </span>
        <span className="ca-order-addr">
          {order.city_district && <span className="ca-district">{order.city_district}</span>}
          {order.delivery_address || 'Адрес не указан'}
          {order.apartment ? `, кв. ${order.apartment}` : ''}
        </span>
        <span className="ca-order-badges">
          {badge}
          {(order.status === 'new' || order.status === 'packaging') && order.florist_name && (
            <span className="ca-employee-badge ca-florist">🌸 {order.florist_name}</span>
          )}
          {(order.status === 'new' || order.status === 'packaging') && order.packer_name && (
            <span className="ca-employee-badge ca-packer">📦 {order.packer_name}</span>
          )}
          {showCourier && order.courier_name && (
            <span className="ca-courier-badge">{order.courier_name}</span>
          )}
        </span>
        <span className="ca-order-actions">
          {actions}
          {showRemove && clusterId && (
            <button
              className="ca-remove-btn"
              onClick={() => handleRemoveFromCluster(clusterId, order.id)}
              title="Убрать из группы"
            >
              &times;
            </button>
          )}
          {renderStatusActions(order)}
        </span>
      </div>
    );
  };

  // От чего считается ETA группы: позиция курьера → выдача → «в сети» → план
  // Подпись: событие (выдан / закрыл точку / позиция) и от какого времени считается ETA —
  // никогда не раньше «сейчас», поэтому «выдан 12:13 · от 14:55»
  const basisLabel = (route) => {
    if (!route?.eta_basis) return null;
    const ev = route.basis_time ? ` ${route.basis_time}` : '';
    const from = route.route_start && route.route_start !== route.basis_time ? ` · от ${route.route_start}` : '';
    const clampNote = ' Отсчёт ETA — не раньше текущего времени.';
    return {
      live: { text: `📍 от позиции курьера${ev}`, title: 'Курьер передаёт позицию — маршрут и ETA от его текущего места и текущего времени' },
      position: { text: `от последней позиции${ev}${from}`, title: `Свежей позиции нет — маршрут от последней известной за сегодня (курьер в пути с заказами).${clampNote}` },
      progress: { text: `от последней точки${ev}${from}`, title: `Часть заказов доставлена — маршрут от последнего закрытого адреса (курьер в пути с остальными).${clampNote}` },
      started: { text: `в сети с${ev}${from}`, title: `Курьер передавал позицию сегодня, свежей нет.${clampNote}` },
      issued: { text: `выдан${ev}${from}`, title: `Заказы переведены «У курьера» в это время; маршрут от склада.${clampNote}` },
      to_warehouse: { text: `едет на склад${ev}`, title: 'Партия ещё не выдана, курьер в дороге к складу: маршрут от склада, отсчёт — с его прибытия туда (по прямой, 30 км/ч)' },
      slot: { text: `план от${route.route_start ? ` ${route.route_start}` : ''}`, title: 'Курьер ещё не выехал: ETA от начала слота, а если слот уже идёт — от текущего времени' },
    }[route.eta_basis] || null;
  };

  // Назначенные заказы по курьерам — из /assigned-routes (порядок объезда, ETA, откуда
  // считается, опоздание); используется и до группировки, и после
  const renderAssignedSection = (title) => {
    if (assignedRoutes.length === 0) return null;
    const total = assignedRoutes.reduce((s, g) => s + g.orders.length, 0);
    return (
      <>
        <div className="ca-section-header ca-section-assigned">{title} <span className="cnt">{total}</span></div>
        <div className="ca-clusters">
          {assignedRoutes.map((route, idx) => {
            const color = clusterColor((built ? clusters.length : 0) + idx);
            const basis = basisLabel(route);
            const done = route.orders.length - (route.active_count || 0);
            return (
              <div
                key={route.courier_id}
                className={`ca-cluster-card ca-assigned-card ${hoverGroup === `a${route.courier_id}` ? 'hovered' : ''}`}
                style={{ '--band': color }}
                onMouseEnter={() => setHoverGroup(`a${route.courier_id}`)}
                onMouseLeave={() => setHoverGroup(null)}
              >
                <div className="ca-cluster-header ca-assigned-header">
                  <div className="ca-cluster-info">
                    <span className="ca-cluster-title">
                      {route.courier_name}
                    </span>
                    <span className="ca-cluster-meta">
                      {route.orders.length} заказов{done > 0 && <> (доставлено {done})</>}
                      {route.total_distance_km != null && (
                        <> &middot; {route.total_distance_km} км &middot; ~{route.total_duration_min} мин</>
                      )}
                      {route.return_km != null && (
                        <span className="ca-return ca-tip" data-tip="Обратный путь с последней точки на склад. В километры и минуты маршрута не входит, но при построении маршрута учитывается">
                          &nbsp;&middot; +{route.return_km} км до склада
                        </span>
                      )}
                      {route.traffic_factor > 1 && (
                        <span className="ca-return ca-tip" data-tip={trafficTip}>
                          &nbsp;&middot; пробки +{Math.round((route.traffic_factor - 1) * 100)}%
                        </span>
                      )}
                      {basis && (
                        <span className={`ca-basis ca-basis-${route.eta_basis} ca-tip`} data-tip={basis.title}> &middot; {basis.text}</span>
                      )}
                      {route.slot_overrun_min > 0 && (
                        <span className="ca-overrun ca-tip" data-tip="По прогнозу последняя точка позже конца слота — пора звонить клиенту или перекидывать заказ другому курьеру">
                          &nbsp;⚠ опоздание на {route.slot_overrun_min} мин
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="ca-cluster-actions">
                    <button
                      className="ca-small-btn"
                      onClick={() => soloGroup(`a${route.courier_id}`)}
                      title="Показать на карте только этого курьера"
                    >
                      На карте
                    </button>
                    {route.active_count >= 2 && (
                      <button
                        className="ca-small-btn"
                        onClick={() => handleRecalcAssigned(route)}
                        title="Пересчитать порядок объезда оставшихся точек по дорогам и сохранить"
                      >
                        Пересчитать маршрут
                      </button>
                    )}
                  </div>
                </div>
                <div className="ca-order-list">
                  {route.orders.map(order => renderOrderRow(order, { showSeq: true, seqColor: color }))}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // Сетка из настроек + слоты загруженных заказов: старые заказы
  // с архивным слотом не должны выпадать из фильтра
  const TIME_SLOTS = unionWithOrderSlots(configSlots, slotOrders);

  // Панель: число курьеров, степпер маршрутов, строка статуса, липкий бар
  const activeInternal = couriers.filter(c => !c.is_external).length;
  const activeCouriersShown = built && numActiveCouriers > 0 ? numActiveCouriers : activeInternal;
  // Курьеры, у которых в этом слоте уже есть маршрут (по /assigned-routes): «Авто» = свободные
  const busyById = useMemo(() => {
    const m = {};
    assignedRoutes.forEach(g => {
      const n = g.orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length;
      if (n > 0) m[g.courier_id] = n;
    });
    return m;
  }, [assignedRoutes]);
  const busyInternal = couriers.filter(c => !c.is_external && busyById[c.id]).length;
  const autoRoutes = Math.max(1, activeInternal - busyInternal);
  const routesValue = parseInt(numClustersInput, 10) || null;
  const courierOptions = useMemo(() => couriers.map(c => ({
    value: String(c.id),
    label: c.name,
    hint: c.is_external ? 'внешний' : (busyById[c.id] ? `на маршруте · ${busyById[c.id]}` : null),
  })), [couriers, busyById]);
  const stepRoutes = (delta) => {
    const cur = routesValue || autoRoutes;
    const next = Math.min(20, Math.max(1, cur + delta));
    setNumClustersInput(String(next));
  };
  const totalSlotCount = Object.values(slotCounts).reduce((a, b) => a + b, 0);
  const unassignedInSlot = slotOrders.filter(o => !o.courier_id).length;
  const groupsTotal = clusters.length + distantOrders.length + unassignable.length + freeOrders.length;
  const stickyVisible = built && groupsTotal > 0;
  // Статусная строка: короткие куски, каждый без переноса внутри — переносится
  // между ними, а не посреди фразы
  const statusChunks = built
    ? [`${clusters.length} ${clusters.length === 1 ? 'группа' : clusters.length < 5 ? 'группы' : 'групп'}`, `курьер выбран у ${assignedCount}`]
    : (slotLoaded ? [`${slotOrders.length} заказов`, `без курьера ${unassignedInSlot}`] : []);

  // Клавиатура: ← → дата, G — сгруппировать, Enter — назначить, Esc — показать все группы
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (e.target && e.target.isContentEditable)) return;
      if (e.target && e.target.closest && e.target.closest('.ca-select, .ca-select-list')) return;
      // Фокус на кнопке: Enter/пробел — это её нажатие, а не «назначить курьеров»
      if (tag === 'button' && (e.key === 'Enter' || e.key === ' ')) return;
      if (showOrderModal || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPrevious(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToNext(); }
      else if (e.key === 'Escape') { if (hiddenGroups.length) showAllGroups(); setShowSettings(false); }
      else if (e.key === 'Enter' && stickyVisible && assignedCount > 0 && !assigning) { e.preventDefault(); handleAssign(); }
      else if ((e.key === 'g' || e.key === 'G' || e.key === 'п' || e.key === 'П') && !loading) { e.preventDefault(); handleBuildClusters(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="ca-page">
      {/* Заголовок */}
      <div className="ca-header">
        <div className="ca-title">Курьеры</div>
      </div>

      {/* Липкая панель: контекст (дата, слот) + действие (маршрутов, настройки, кнопка этапа) */}
      <div className="ca-panel">
        <div className="ca-panel-row">
          {/* ‹ Сегодня › — одна группа: срез у крайних, середина прямая (владелец 25.10) */}
          <div className="ca-datenav seg" role="group" aria-label="День">
            <button type="button" className="seg-btn ca-datenav-arrow" onClick={goToPrevious} title="Предыдущий день (←)" aria-label="Предыдущий день">{I.prev}</button>
            <button type="button" className="seg-btn" onClick={goToToday}>Сегодня</button>
            <button type="button" className="seg-btn ca-datenav-arrow" onClick={goToNext} title="Следующий день (→)" aria-label="Следующий день">{I.next}</button>
          </div>
          <div className="ca-date-label">{formatDate(selectedDate)}</div>
          <div className="ca-status">
            <span className="chunk" title={busyInternal > 0 ? `${busyInternal} уже получили маршрут в этом слоте` : 'Активные курьеры (внешние не считаются)'}>
              {busyInternal > 0
                ? <>свободны <strong>{Math.max(0, activeCouriersShown - busyInternal)}</strong> из {activeCouriersShown} курьеров</>
                : <>курьеров <strong>{activeCouriersShown}</strong></>}
            </span>
            {statusChunks.map((c) => <span key={c} className="chunk">{c}</span>)}
          </div>
          <div className="ca-actions">
            <span className="ca-stepper-label">Маршрутов</span>
            <div className="ca-stepper">
              <button type="button" onClick={() => stepRoutes(-1)} title="Меньше маршрутов">{I.minus}</button>
              <button
                type="button"
                className={`ca-stepper-val ${routesValue ? '' : 'auto'}`}
                onClick={() => setNumClustersInput('')}
                title={routesValue
                  ? 'Сбросить на Авто'
                  : `Авто = активные курьеры без маршрута в этом слоте: ${activeInternal}${busyInternal ? ` − ${busyInternal} уже на маршруте` : ''}; внешние не считаются`}
              >
                {routesValue || `Авто · ${autoRoutes}`}
              </button>
              <button type="button" onClick={() => stepRoutes(1)} title="Больше маршрутов">{I.plus}</button>
            </div>
            {balanced && <span className="ca-tag" title="Равномерно по курьерам">равномерно</span>}
            <div className="ca-popover-anchor">
              <button
                type="button"
                className={`ca-icon-btn ${showSettings ? 'active' : ''}`}
                onClick={() => setShowSettings(v => !v)}
                title="Настройки группировки"
              >
                {I.sliders}
              </button>
              {showSettings && (
                <div className="ca-popover">
                  <div className="ca-popover-title">Настройки группировки</div>
                  <div className="ca-popover-row" onClick={() => setBalanced(b => !b)}>
                    <div>
                      <div className="lbl">Равномерно по курьерам</div>
                      <div className="hint">Поровну заказов, даже если по километрам выгоднее иначе</div>
                    </div>
                    <span className={`ca-toggle ${balanced ? 'on' : ''}`} />
                  </div>
                  <div className="ca-popover-note">
                    «Авто» = все активные курьеры. Внешнюю доставку (Яндекс и т. п.) отметьте
                    «внешний» в «Сотрудниках» — она в счёт не входит. Время на вручение — 7 мин на точку.
                  </div>
                </div>
              )}
            </div>
            {built ? (
              <button className="ca-outline-btn" onClick={handleBuildClusters} disabled={loading} title="Пересобрать группы из неназначенных заказов (G)">
                {I.refresh}{loading ? 'Считаем…' : 'Пересобрать группы'}
              </button>
            ) : (
              <button className="ca-primary-btn" onClick={handleBuildClusters} disabled={loading} title="Сгруппировать неназначенные заказы (G)">
                {I.grid}{loading ? 'Считаем…' : 'Сгруппировать'}
              </button>
            )}
          </div>
        </div>

        <div className="ca-panel-sep" />

        <div className="ca-panel-row ca-panel-row-slots">
          {/* Слоты — один переключатель .seg: срез только у контейнера, между слотами
              разделители, без промежутков (владелец 25.10) */}
          <div className="ca-slots seg" role="group" aria-label="Слот доставки">
            <button
              type="button"
              className={`seg-btn${selectedSlot === null ? ' on' : ''}`}
              onClick={() => handleSlotClick(null)}
            >
              Все слоты{totalSlotCount > 0 && <span className="ca-slot-count">{totalSlotCount}</span>}
            </button>
            {TIME_SLOTS.map(slot => {
              const n = slotCounts[slot] || 0;
              return (
                <button
                  key={slot}
                  type="button"
                  className={`seg-btn${selectedSlot === slot ? ' on' : ''}${n === 0 ? ' empty' : ''}`}
                  onClick={() => handleSlotClick(slot)}
                  title={slot}
                >
                  {shortSlot(slot)}{n > 0 && <span className="ca-slot-count">{n}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* === Карта: легенда и статус живут на ней === */}
      {(built || slotLoaded) && (showMap ? (
        <div className="ca-map-wrap">
          <RouteMap
            warehouse={warehouse}
            clusters={visibleClusters}
            points={visiblePoints}
            couriers={liveCouriers}
            onOrderClick={openOrderModal}
            fitKey={mapFitKey}
            highlightId={hoverGroup}
          />
          {mapClusters.length > 0 && (
            <div className="ca-map-legend-float">
              <div className="ca-legend-head">
                <span>{built ? 'Группы на карте' : 'Курьеры на карте'}</span>
                {hiddenGroups.length > 0 && <button type="button" onClick={showAllGroups}>все</button>}
              </div>
              {mapClusters.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={`ca-legend-row ${isHidden(c.id) ? 'hidden' : ''} ${hoverGroup === c.id ? 'hovered' : ''}`}
                  style={{ '--swatch': c.color }}
                  onClick={(e) => (e.altKey ? soloGroup(c.id) : toggleGroup(c.id))}
                  onMouseEnter={() => setHoverGroup(c.id)}
                  onMouseLeave={() => setHoverGroup(null)}
                  title={isHidden(c.id) ? 'Показать на карте' : 'Скрыть с карты · Alt+клик — только эта'}
                >
                  <i style={{ background: c.color }} /><span className="lbl">{c.label}</span><span className="cnt">{c.orders.length}</span>
                </button>
              ))}
              {mapPoints.length > 0 && (
                <>
                  <div className="ca-legend-sep" />
                  <button
                    type="button"
                    className={`ca-legend-row ${isHidden('points') ? 'hidden' : ''}`}
                    style={{ '--swatch': '#909894' }}
                    onClick={(e) => (e.altKey ? soloGroup('points') : toggleGroup('points'))}
                    title={isHidden('points') ? 'Показать на карте' : 'Скрыть с карты'}
                  >
                    <i style={{ background: '#909894' }} /><span className="lbl">Без группы</span><span className="cnt">{mapPoints.length}</span>
                  </button>
                </>
              )}
              <div className="ca-legend-hint">Alt + клик — только эта группа</div>
            </div>
          )}
          <div className="ca-map-topright">
            <span className={`ca-online ${onlineCount === 0 ? 'off' : ''}`} title="Курьеры, передающие позицию (последние 10 минут)">
              <i />Онлайн {onlineCount}/{activeCouriersShown}
            </span>
            <button className="ca-icon-btn" onClick={() => setShowMap(false)} title="Свернуть карту">{I.up}</button>
          </div>
        </div>
      ) : (
        <div className="ca-map-collapsed">
          <span>Карта скрыта</span>
          <span>Онлайн {onlineCount}/{activeCouriersShown}</span>
          <button className="ca-small-btn" onClick={() => setShowMap(true)}>Показать карту</button>
        </div>
      ))}

      {/* === FLAT ORDER LIST (before clustering) === */}
      {slotLoaded && !built && (
        <div className="ca-slot-orders">
          {slotOrders.length === 0 && assignedRoutes.length === 0 ? (
            <div className="ca-empty">Нет заказов{selectedSlot ? ` на слот ${selectedSlot}` : ''}</div>
          ) : (
            <>
              {renderAssignedSection('По курьерам')}
              {(() => {
                const free = slotOrders.filter(o => !o.courier_id);
                if (free.length === 0) return null;
                return (
                  <>
                    <div className="ca-section-header">Без курьера <span className="cnt">{free.length} · нажмите «Сгруппировать»</span></div>
                    <div className="ca-cluster-card">
                      <div className="ca-order-list">
                        {free.map(order => renderOrderRow(order))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {slotLoading && (
        <div className="ca-empty">Загрузка...</div>
      )}

      {/* === CLUSTERED RESULTS === */}
      {built && (
        <>
          {clusters.length === 0 && distantOrders.length === 0 && unassignable.length === 0 && assignedGroups.length === 0 && (
            <div className="ca-empty">Нет заказов для распределения на эту дату</div>
          )}

          {/* New clusters */}
          {clusters.length > 0 && (
            <div className="ca-section-header">Группы для назначения <span className="cnt">{clusters.reduce((s, c) => s + c.order_count, 0)} заказов</span></div>
          )}
          <div className="ca-clusters">
            {clusters.map((cluster, idx) => (
              <div
                key={cluster.cluster_id}
                className={`ca-cluster-card ${hoverGroup === `c${cluster.cluster_id}` ? 'hovered' : ''}`}
                style={{ '--band': clusterColor(idx) }}
                onMouseEnter={() => setHoverGroup(`c${cluster.cluster_id}`)}
                onMouseLeave={() => setHoverGroup(null)}
              >
                <div className="ca-cluster-header">
                  <div className="ca-cluster-info">
                    <span className="ca-cluster-title">
                      {cluster.solver_unassigned ? 'Не уложились' : `Группа ${cluster.cluster_id}`}
                      {cluster.solver_unassigned && (
                        <span className="ca-engine-badge warn" title={`Решатель не вписал эти заказы в ${routesValue || autoRoutes} маршрут(а) с их окнами. Это не группа курьера: добавьте маршрут (+), перенесите заказ в группу («×» → «В группу…») или в другой слот`}>
                          сверх {routesValue || autoRoutes} маршрутов
                        </span>
                      )}
                    </span>
                    <span className="ca-cluster-meta">
                      {cluster.order_count} заказов &middot; {cluster.total_distance_km} км &middot; ~{cluster.total_duration_min} мин
                      {cluster.return_km != null && (
                        <span className="ca-return ca-tip" data-tip="Обратный путь с последней точки на склад. В километры и минуты группы не входит, но решатель его учитывает — поэтому последняя точка тянется к складу">
                          &nbsp;&middot; +{cluster.return_km} км до склада
                        </span>
                      )}
                      {cluster.traffic_factor > 1 && (
                        <span className="ca-return ca-tip" data-tip={trafficTip}>
                          &nbsp;&middot; пробки +{Math.round((cluster.traffic_factor - 1) * 100)}%
                        </span>
                      )}
                      {cluster.orders.filter(o => o.eta_late).length > 0 && (
                        <span className="ca-overrun ca-tip" data-tip="Прогноз прибытия позже обещанного — конца слота или «не позже» клиента; в списке их ETA красным. Что делать: добавить маршрут (+1 и пересобрать), перенести заказ в другую группу или слот, либо согласовать время с клиентом">
                          &nbsp;⚠ опаздыва{cluster.orders.filter(o => o.eta_late).length === 1 ? 'ет' : 'ют'} {cluster.orders.filter(o => o.eta_late).length}
                        </span>
                      )}
                      {cluster.slot_overrun_min > 0 && (
                        <span className="ca-overrun ca-tip" data-tip="Последняя точка по прогнозу позже конца слота. Уберите адрес из группы («×»), добавьте маршрут (+1) или согласуйте время с клиентом">
                          &nbsp;⚠ превышает слот на {cluster.slot_overrun_min} мин
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="ca-cluster-actions">
                    <button
                      className="ca-small-btn"
                      onClick={() => soloGroup(`c${cluster.cluster_id}`)}
                      title="Показать на карте только эту группу"
                    >
                      На карте
                    </button>
                    <CaSelect
                      size="md"
                      placeholder="Курьер"
                      options={courierOptions}
                      value={clusterCouriers[cluster.cluster_id] || ''}
                      onChange={(v) => setClusterCouriers(prev => ({ ...prev, [cluster.cluster_id]: v }))}
                    />
                  </div>
                </div>

                <div className="ca-order-list">
                  {cluster.orders.map(order => renderOrderRow(order, { showSeq: true, showRemove: true, clusterId: cluster.cluster_id, seqColor: clusterColor(idx) }))}
                </div>
              </div>
            ))}
          </div>

          {/* Free orders (removed from groups) */}
          {freeOrders.length > 0 && (
            <div className="ca-free-orders">
              <div className="ca-free-header">
                Свободные заказы ({freeOrders.length})
              </div>
              <div className="ca-order-list">
                {freeOrders.map(order => renderOrderRow(order, {
                  actions: (
                    <>
                      {clusters.length > 0 && (
                        <span className="ca-add-to-group">
                          <CaSelect
                            size="sm"
                            placeholder="В группу…"
                            options={clusters.map(c => ({
                              value: String(c.cluster_id),
                              label: `Группа ${c.cluster_id}`,
                              hint: order.recommended_cluster === c.cluster_id ? 'ближайшая' : null,
                            }))}
                            value={freeGroupAssign[order.id] || ''}
                            onChange={(v) => setFreeGroupAssign(prev => ({ ...prev, [order.id]: v }))}
                          />
                          {freeGroupAssign[order.id] && (
                            <button
                              className="ca-add-btn"
                              onClick={() => handleAddToCluster(order.id, freeGroupAssign[order.id])}
                              title="Добавить в группу и пересчитать её маршрут"
                            >
                              +
                            </button>
                          )}
                        </span>
                      )}
                      <CaSelect
                        size="sm"
                        placeholder="Курьер"
                        options={courierOptions}
                        value={freeCouriers[order.id] || ''}
                        onChange={(v) => setFreeCouriers(prev => ({ ...prev, [order.id]: v }))}
                      />
                    </>
                  ),
                }))}
              </div>
            </div>
          )}

          {/* Distant orders */}
          {distantOrders.length > 0 && (
            <div className="ca-distant">
              <div className="ca-distant-header">
                Дальние заказы — за радиусом доставки {distantKm} км ({distantOrders.length})
                <span className="ca-tip" data-tip="Радиус доставки из настроек (Настройки → доставка). Такой заказ в группы автоматически не попадает: отдайте его в группу («В группу…» — маршрут пересчитается) или назначьте курьера напрямую" style={{ marginLeft: 8, fontWeight: 400 }}>?</span>
              </div>
              <div className="ca-order-list">
                {distantOrders.map(order => renderOrderRow(order, {
                  actions: (
                    <>
                      {clusters.length > 0 && (
                        <span className="ca-add-to-group">
                          <CaSelect
                            size="sm"
                            placeholder="В группу…"
                            options={clusters.map(c => ({
                              value: String(c.cluster_id),
                              label: `Группа ${c.cluster_id}`,
                              hint: order.nearest_cluster_id === c.cluster_id ? 'ближайшая' : null,
                            }))}
                            value={freeGroupAssign[order.id] || ''}
                            onChange={(v) => setFreeGroupAssign(prev => ({ ...prev, [order.id]: v }))}
                          />
                          {freeGroupAssign[order.id] && (
                            <button
                              className="ca-add-btn"
                              onClick={() => handleAddToCluster(order.id, freeGroupAssign[order.id])}
                              title="Добавить в группу и пересчитать её маршрут"
                            >
                              +
                            </button>
                          )}
                        </span>
                      )}
                      <CaSelect
                        size="sm"
                        placeholder="Курьер"
                        options={courierOptions}
                        value={distantCouriers[order.id] || ''}
                        onChange={(v) => setDistantCouriers(prev => ({ ...prev, [order.id]: v }))}
                      />
                    </>
                  ),
                }))}
              </div>
            </div>
          )}

          {/* Unassignable (no coords) */}
          {unassignable.length > 0 && (
            <div className="ca-unassignable">
              <div className="ca-unassignable-header">
                Не удалось определить адрес ({unassignable.length})
              </div>
              <div className="ca-order-list">
                {unassignable.map(order => renderOrderRow(order, {
                  actions: (
                    <CaSelect
                      size="sm"
                      placeholder="Курьер"
                      options={courierOptions}
                      value={manualCouriers[order.id] || ''}
                      onChange={(v) => setManualCouriers(prev => ({ ...prev, [order.id]: v }))}
                    />
                  ),
                }))}
              </div>
            </div>
          )}

          {/* Липкий бар назначения: прогресс + кнопка этапа */}
          {stickyVisible && (
            <div className="ca-sticky-bar">
              <div className="ca-progress"><i style={{ width: `${Math.round(assignedCount / groupsTotal * 100)}%` }} /></div>
              <span className="ca-sticky-text">Курьер выбран у <strong>{assignedCount} из {groupsTotal}</strong> {groupsTotal === 1 ? 'группы' : 'групп'}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="ca-kbd">Enter — назначить</span>
                <button
                  className="ca-cta"
                  onClick={handleAssign}
                  disabled={assigning || assignedCount === 0}
                  title={assignedCount === 0 ? 'Выберите курьера хотя бы одной группе' : ''}
                >
                  {assigning ? 'Назначаем…' : `Назначить курьеров — ${assignedCount} из ${groupsTotal}`}
                </button>
              </div>
            </div>
          )}

          {/* Assigned orders — по курьерам, с ETA и точкой отсчёта */}
          {renderAssignedSection('Назначенные заказы')}
        </>
      )}
      {showOrderModal && selectedOrder && (
        <OrderModal
          initialOrder={selectedOrder}
          onClose={() => setShowOrderModal(false)}
          showToast={showToast}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
}

export default CourierAssignmentPage;
